/**
 * Ticker — clickable symbol badge that opens the rich Opportunity Drawer.
 *
 * Historically this file also owned a small right-side drawer, but users
 * asked "why is there a tiny preview when the Opportunity Drawer already
 * has everything I need?". Answer: consolidated. The <Ticker> badge now
 * dispatches the same `tm:open-drawer` event that Opportunities uses, so
 * every symbol across the app opens the same rich 13-tab experience
 * (Overview, Why, AI Debate, Risk, Chart, Fundamentals, Technical, News,
 * Memory, History, Trade Plan, Related, Notes).
 *
 * Kept for API-compatibility: <TickerDrawerProvider> is still exported so
 * <App/> doesn't need to change, and useTickerDrawer() still works.
 */
import { createContext, useCallback, useContext, useMemo } from "react";

const TickerDrawerCtx = createContext({ openTicker: () => {} });

export function useTickerDrawer() {
  return useContext(TickerDrawerCtx);
}

/**
 * Clickable symbol badge. Use anywhere a raw symbol string appears.
 * <Ticker symbol="AAPL" />  →  opens the rich Opportunity Drawer.
 */
export function Ticker({ symbol, children, className = "", size = "sm", tab = "overview", ...rest }) {
  const { openTicker } = useTickerDrawer();
  if (!symbol) return null;
  const sizeCls = size === "xs" ? "text-[10px] px-1.5 py-0"
                : size === "lg" ? "text-sm px-2 py-1"
                : "text-xs px-1.5 py-0.5";
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); openTicker(symbol, tab); }}
      className={`inline-flex items-center gap-1 mono uppercase border border-border/60 hover:border-signal-blue hover:text-signal-blue transition-colors ${sizeCls} ${className}`}
      data-testid={`ticker-${symbol}`}
      {...rest}
    >
      {children || symbol}
    </button>
  );
}

export function TickerDrawerProvider({ children }) {
  // Delegates to the globally-mounted <GlobalOpportunityDrawer> via the
  // existing `tm:open-drawer` custom event. No local Sheet, no duplicate
  // yfinance fetches.
  const openTicker = useCallback((s, tab = "overview") => {
    if (!s) return;
    window.dispatchEvent(new CustomEvent("tm:open-drawer", {
      detail: { symbol: String(s).toUpperCase(), tab },
    }));
  }, []);

  const ctx = useMemo(() => ({ openTicker }), [openTicker]);

  return (
    <TickerDrawerCtx.Provider value={ctx}>
      {children}
    </TickerDrawerCtx.Provider>
  );
}
